import authService from './authService';
import { ApiViewResponseError, ApiViewResponse } from '../interfaces';
import { logger } from './logger';

// Renew slightly before expiry so a token doesn't die mid-flight; also absorbs
// modest clock skew (expiry is checked against the local clock).
const EXPIRY_BUFFER_SECONDS = 30;

class ApiService {
  public fetch(url: RequestInfo, opts: RequestInit, retry?: boolean): Promise<Response>;
  public fetch(url: RequestInfo, formData: FormData, retry?: boolean): Promise<Response>;
  public async fetch(url: RequestInfo, optsOrFormData: RequestInit | FormData, retry: boolean = false): Promise<Response> {
    const user = await this.getFreshUser();
    const isFormData = optsOrFormData instanceof FormData;
    const config: RequestInit = isFormData
      ? { method: 'POST', body: optsOrFormData, headers: { 'Authorization': `bearer ${user?.access_token}` } }
      : { ...optsOrFormData, headers: { 'Content-Type': 'application/json', 'Authorization': `bearer ${user?.access_token}` } };

    let res: Response;
    try {
      res = await fetch(url, config);
    } catch (err: any) {
      logger.error('API request failed.', err);
      throw err;
    }

    // Fallback for anything the local expiry check can't see (token revoked
    // server-side, key rollover, clock skew): renew once and retry.
    if (res.status === 401 && !retry) {
      await this.renewOrLogin();
      return isFormData
        ? this.fetch(url, optsOrFormData as FormData, true)
        : this.fetch(url, optsOrFormData as RequestInit, true);
    }

    if (res.ok) {
      return res;
    }
    throw res;
  }

  // Proactive renewal: token expiry is checked locally (oidc-client-ts derives
  // expired/expires_in from the stored expires_at) — no network round-trip.
  private async getFreshUser() {
    let user = await authService.getUser();
    if (!user || user.expired || (user.expires_in !== undefined && user.expires_in < EXPIRY_BUFFER_SECONDS)) {
      await this.renewOrLogin();
      user = await authService.getUser();
    }
    return user;
  }

  private async renewOrLogin(): Promise<void> {
    try {
      await authService.renewToken();
    } catch (err) {
      logger.error('Token renewal failed, redirecting to sign-in.', err);
      await authService.removeUser();
      await authService.login({ state: { returnUrl: window.location.pathname + window.location.search } });
      throw new Error('Your session has expired. Redirecting to sign-in.');
    }
  }
}

export const getBodyErrors = async (err: any): Promise<ApiViewResponseError[]> => {
  if (err.status === 403) {
    return [{
      detail: 'You do not have permission to perform this action.', title: 'Forbidden', code: '', id: '', status: ''
    }];
  }
  if (err.body) {
    const apiResponseBody = await err.json() as ApiViewResponse;
    return (apiResponseBody.errors);
  }
  return [{
      detail: err.message ?? err.toString(), title: 'Error', code: '', id: '', status: ''
  }];
}

const apiService = new ApiService();
export default apiService;
